/* ============================================================
   Course-creation helpers for the StudyAK chat assistant.

   Flow:
     1. buildCoursePrompt(name, grade)  -> LLM prompt that asks for
        JSON matching the project's "add course" payload.
     2. generateCourse(name, grade)     -> calls the LLM, parses the
        JSON, and normalises it into the exact API shape. Falls back
        to a locally-built outline when no AI key is configured so the
        flow keeps working out of the box.
     3. addCourse(course)               -> POSTs to the same endpoint
        course.js uses (/api/course/coursedomain/{userId}&{orgId}).
   ============================================================ */

import { generatePrompt } from '../Instructor/assignment/generateprompt';
import {
  GenerateQuestions,
  GenerateQuestionsFromSpec,
} from '../Instructor/assignment/generatequestions';
import { splitPassage } from '../Instructor/assignment/passageFormat';

const apiUrl = process.env.REACT_APP_API_URL;

const getBasicAuthHeader = () => {
  const username = process.env.REACT_APP_API_USERNAME;
  const password = process.env.REACT_APP_API_PASSWORD;
  return `Basic ${btoa(`${username}:${password}`)}`;
};

/**
 * Fetches a finished prompt from the backend prompt store (Settings → Prompt).
 * The template text and {{variable}} interpolation live server-side, so
 * super-admin edits take effect on the next request without any code change.
 * Throws if the request fails so callers can fall back as they see fit.
 */
export const renderServerPrompt = async (key, vars = {}) => {
  const res = await fetch(`${apiUrl}/api/prompts/render`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: '*/*',
      Authorization: getBasicAuthHeader(),
    },
    body: JSON.stringify({ key, vars }),
  });
  if (!res.ok) {
    throw new Error(`Prompt render failed for "${key}" (${res.status})`);
  }
  const data = await res.json();
  return (data && data.prompt) || '';
};

/* ---------- Chatbot token usage tracking ----------
   Rough cost estimate (USD) by model — adjust per your contracted rates.
   Rates are per 1K tokens [prompt, completion]. */
const TOKEN_RATES = {
  'gpt-4o-mini': [0.00015, 0.0006],
  default: [0.0005, 0.0015],
};

export const estimateTokenCost = (model, promptTokens = 0, completionTokens = 0) => {
  const [pr, cr] = TOKEN_RATES[model] || TOKEN_RATES.default;
  return +(((promptTokens / 1000) * pr) + ((completionTokens / 1000) * cr)).toFixed(6);
};

/**
 * Persist a chatbot token-usage record.
 * Endpoint + body for the API team:
 *   POST {API}/api/token-usage
 *   body: { userId, courseId, assessmentId, model, promptTokens, completionTokens, totalTokens, cost, source }
 */
export const saveTokenUsage = async ({
  userId = localStorage.getItem('userId'),
  courseId = null,
  assessmentId = null,
  model = '',
  promptTokens = 0,
  completionTokens = 0,
  totalTokens = 0,
  cost = 0,
  source = 'chatbot',
} = {}) => {
  try {
    await fetch(`${apiUrl}/api/token-usage`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: '*/*',
        'Access-Control-Allow-Origin': '*',
        Authorization: getBasicAuthHeader(),
      },
      body: JSON.stringify({
        userId, courseId, assessmentId, model,
        promptTokens, completionTokens,
        totalTokens: totalTokens || promptTokens + completionTokens,
        cost: cost || estimateTokenCost(model, promptTokens, completionTokens),
        source,
      }),
    });
  } catch (e) {
    console.warn('[StudyAK chat] token usage save failed:', e);
  }
};

// 10-char id, same scheme as course/dialog.js + CourseWizard.js
export const generateDomainId = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let id = '';
  for (let i = 0; i < 10; i++) id += chars.charAt(Math.floor(Math.random() * chars.length));
  return id;
};

// Map a free-text grade/level onto the course types the API expects.
// Grades K–12 -> "K-12"; university/college -> "University"; else "Certification".
export const inferCourseType = (grade = '') => {
  const g = String(grade).toLowerCase();
  if (/(university|college|undergrad|bachelor|master|degree)/.test(g)) return 'University';
  if (/(certification|professional|exam|license|licence|diploma)/.test(g)) return 'Certification';
  return 'K-12';
};

/* ---------- 1. Prompt builder ---------- */
// The prompt text now lives in the backend prompt store (key: course.generate)
// and is editable from Settings → Prompt. Interpolation happens server-side.
export const buildCoursePrompt = (courseName, grade) => {
  const courseType = inferCourseType(grade);
  return renderServerPrompt('course.generate', { courseName, grade, courseType });
};

/**
 * Coerces the model's courseCategory onto exactly 'Technical' or 'Non-Technical'.
 * Anything unrecognised (or a prompt edited to drop the field) falls back to
 * 'Non-Technical' — the broader four-category question mix.
 */
const normaliseCourseCategory = (value) =>
  (String(value || '').trim().toLowerCase() === 'technical' ? 'Technical' : 'Non-Technical');

/**
 * Coerces the model's subjectArea onto exactly 'ELA' or 'Other'. Anything
 * unrecognised falls back to 'Other', i.e. the standard question mix.
 */
const normaliseSubjectArea = (value) =>
  (String(value || '').trim().toLowerCase() === 'ela' ? 'ELA' : 'Other');

/** K-12 English Language Arts — passage/image-only question mix. */
const isK12Ela = (course = {}) =>
  String(course.courseType || '').trim().toLowerCase() === 'k-12'
  && normaliseSubjectArea(course.subjectArea) === 'ELA';


/* ---------- normalise whatever the model returns into the API shape ---------- */
const normaliseCourse = (raw, courseName, grade) => {
  const domains = Array.isArray(raw?.domains) ? raw.domains : [];
  return {
    courseId: '',
    courseName: raw?.courseName || courseName,
    code: raw?.code || '',
    vendor: raw?.vendor || 'StudyAK',
    start: '',
    expire: '',
    enabled: '',
    level: 0, // integer column; default 0 on create — set later on the course settings page
    gradeLabel: grade, // friendly label for display only (not sent — see addCourse)
    courseType: raw?.courseType || inferCourseType(grade),
    // Technical / Non-Technical and ELA / Other come from the model (course.generate)
    // and decide the automatic question mix. Display/planning only — not sent
    // (see addCourse).
    courseCategory: normaliseCourseCategory(raw?.courseCategory),
    subjectArea: normaliseSubjectArea(raw?.subjectArea),
    about: raw?.about || '',
    domains: domains.map((d) => ({
      name: d?.name || '',
      percentage: String(d?.percentage ?? ''),
      domainId: generateDomainId(),
      subDomain: Array.isArray(d?.subDomain)
        ? d.subDomain.map((s) => ({
            name: s?.name || '',
            subTopics: s?.subTopics || '',
            // subDomainId is required (sub_domain_id is NOT NULL). The auto flow
            // builds assessments from this in-memory course, and /assessment/create
            // copies the id straight through — so generate one here (like domainId).
            subDomainId: generateDomainId(),
          }))
        : [],
    })),
  };
};

/* ---------- a sensible offline outline when no AI key is set ---------- */
const buildFallbackCourse = (courseName, grade) => {
  const courseType = inferCourseType(grade);
  const gradeCode = String(grade).replace(/[^A-Za-z0-9]/g, '').slice(0, 4).toUpperCase() || 'GEN';
  const stub = String(courseName).replace(/[^A-Za-z]/g, '').slice(0, 4).toUpperCase() || 'CRS';
  return normaliseCourse(
    {
      courseName,
      code: `${stub}-${gradeCode}`,
      vendor: 'StudyAK',
      courseType,
      level: grade,
      about: `An introductory ${courseName} course designed for ${grade}. Learners build core concepts through structured units and guided practice.`,
      domains: [
        {
          name: `Foundations of ${courseName}`,
          percentage: '40',
          subDomain: [
            { name: 'Core concepts', subTopics: 'key terms, principles, overview' },
            { name: 'Getting started', subTopics: 'basics, examples, practice' },
          ],
        },
        {
          name: `Applied ${courseName}`,
          percentage: '35',
          subDomain: [
            { name: 'Practical skills', subTopics: 'methods, techniques, application' },
            { name: 'Problem solving', subTopics: 'exercises, case studies' },
          ],
        },
        {
          name: 'Assessment & review',
          percentage: '25',
          subDomain: [
            { name: 'Revision', subTopics: 'summary, recap, key points' },
            { name: 'Evaluation', subTopics: 'quizzes, projects, feedback' },
          ],
        },
      ],
    },
    courseName,
    grade,
  );
};

const stripJson = (text = '') =>
  text.replace(/^```(?:json)?/i, '').replace(/```$/i, '').trim();

/* ---------- 2. Generate the course (LLM + fallback) ---------- */
export const generateCourse = async (courseName, grade) => {
  const apiUrl = process.env.REACT_APP_API_URL;

  if (!apiUrl) {
    // No backend configured — return a structured outline so the flow still works.
    return buildFallbackCourse(courseName, grade);
  }

  try {
    // Prompt text is fetched from the backend prompt store (course.generate).
    const prompt = await buildCoursePrompt(courseName, grade);
    // Routed through the backend (Integration Plan §10) — the OpenAI key stays
    // server-side rather than being shipped to the browser.
    const auth = `Basic ${btoa(`${process.env.REACT_APP_API_USERNAME}:${process.env.REACT_APP_API_PASSWORD}`)}`;
    const res = await fetch(`${apiUrl}/api/v1/openai/yaki/generate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: auth,
      },
      body: JSON.stringify({ prompt }),
    });
    const data = await res.json();
    const content = Array.isArray(data?.response) ? data.response[0] : data?.response;
    const parsed = JSON.parse(stripJson(content));
    return normaliseCourse(parsed, courseName, grade);
  } catch (err) {
    console.warn('[StudyAK chat] AI generation failed, using fallback outline:', err);
    return buildFallbackCourse(courseName, grade);
  }
};

/* ---------- 2b. Generate the course from an attached PDF / image (+ text) ----------
   Reuses the same course prompt and normaliser as generateCourse, so the
   returned course has the identical shape and the rest of the flow (preview,
   add course, assessments) is unchanged. Attachments are handled by the
   existing backend endpoints:
     - PDF   : POST /api/v1/openai/process-pdf   (multipart file + prompt)
     - image : POST /api/uploadBase64Image (Azure blob) then
               POST /api/v1/openai/send-prompt-with-image (vision)
     - text-only augmentation falls back to the standard yaki/generate. */

const readFileAsDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = () => reject(new Error('Could not read the selected file.'));
  reader.readAsDataURL(file);
});

// Uploads an image to Azure blob storage and returns its public URL.
export const uploadImageToBlob = async (file) => {
  const base64 = await readFileAsDataUrl(file);
  const res = await fetch(`${apiUrl}/api/uploadBase64Image`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: '*/*',
      Authorization: getBasicAuthHeader(),
    },
    body: JSON.stringify({ base64 }),
  });
  if (!res.ok) {
    throw new Error(`Image upload failed (${res.status})`);
  }
  const data = await res.json();
  return data.fileUrl;
};

const isPdfFile = (file) =>
  !!file && (file.type === 'application/pdf' || /\.pdf$/i.test(file.name || ''));

/**
 * Save a PDF/image attachment to Azure blob storage against a saved course
 * (blob storage controller: POST /api/uploadPdf/{courseId}). Returns the blob URL.
 * Best-effort: callers wrap this so a failed save never blocks course creation.
 */
export const uploadCourseAttachment = async (courseId, file, source = 'Instructor') => {
  if (!courseId || !file) return null;
  const form = new FormData();
  form.append('file', file);
  form.append('name', file.name || 'attachment');
  form.append('source', source);
  const res = await fetch(`${apiUrl}/api/uploadPdf/${courseId}`, {
    method: 'POST',
    headers: { Authorization: getBasicAuthHeader() }, // browser sets the multipart boundary
    body: form,
  });
  if (!res.ok) {
    throw new Error(`Attachment save failed (${res.status})`);
  }
  return res.text(); // endpoint returns the blob URL
};

// Base course prompt + optional attachment/text instructions. All three pieces
// are DB-editable (keys: course.generate, course.attachment.instructions,
// course.attachment.extra) and rendered server-side.
const buildAttachmentPrompt = async (courseName, grade, extraText, hasFile) => {
  const parts = [await buildCoursePrompt(courseName, grade)];
  if (hasFile) {
    // When a document is provided, the instructions tell the model to mirror ITS
    // real structure instead of the base prompt's fixed domain/subdomain caps.
    parts.push(await renderServerPrompt('course.attachment.instructions'));
  }
  if (extraText && extraText.trim()) {
    parts.push(await renderServerPrompt('course.attachment.extra', { extraText: extraText.trim() }));
  }
  return parts.join('\n\n');
};

// Robustly pull the course JSON object out of an assistant reply that may include
// code fences, file_search citation markers (【…】), or surrounding prose.
const extractCourseJson = (text = '') => {
  let s = String(text)
    .replace(/```json/gi, '')
    .replace(/```/g, '')
    .replace(/【[^】]*】/g, '')
    .trim();
  const start = s.indexOf('{');
  const end = s.lastIndexOf('}');
  if (start !== -1 && end !== -1 && end > start) {
    s = s.slice(start, end + 1);
  }
  return s.trim();
};

// Parse whatever the assistant returned (string or array of messages) into a
// normalised course. The assistant's answer is the first message.
const parseCourseResponse = (content, courseName, grade) => {
  const text = Array.isArray(content) ? content[0] : content;
  const asString = typeof text === 'string' ? text : JSON.stringify(text);
  const parsed = JSON.parse(extractCourseJson(asString));
  const course = normaliseCourse(parsed, courseName, grade);
  // Guard: never let a malformed/empty reply produce a domain-less course
  // (which would then create an empty course with no assessments).
  if (!course.domains || course.domains.length === 0) {
    throw new Error('The generated course had no domains — please try again.');
  }
  return course;
};

/**
 * Generate a course from an attached PDF or image and/or extra text.
 * Returns the same normalised course shape as generateCourse.
 */
export const generateCourseFromAttachment = async ({ courseName, grade, file, text }) => {
  const userId = localStorage.getItem('userId') || '';
  const prompt = await buildAttachmentPrompt(courseName, grade, text, !!file);

  // PDF → OpenAI file_search via /process-pdf (multipart).
  if (isPdfFile(file)) {
    const form = new FormData();
    form.append('file', file);
    form.append('prompt', prompt);
    form.append('userId', userId);
    const res = await fetch(`${apiUrl}/api/v1/openai/process-pdf`, {
      method: 'POST',
      headers: { Authorization: getBasicAuthHeader() }, // let the browser set the multipart boundary
      body: form,
    });
    if (!res.ok) {
      throw new Error(`process-pdf failed (${res.status})`);
    }
    // process-pdf returns a JSON wrapper: { response: ["<assistant course JSON>", ...], ... }
    const data = await res.json();
    return parseCourseResponse(data.response, courseName, grade);
  }

  // Image → blob upload, then vision prompt via /send-prompt-with-image.
  if (file) {
    const imageUrl = await uploadImageToBlob(file);
    const res = await fetch(`${apiUrl}/api/v1/openai/send-prompt-with-image`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: '*/*',
        Authorization: getBasicAuthHeader(),
      },
      body: JSON.stringify({ prompt, imageUrl, userId }),
    });
    if (!res.ok) {
      throw new Error(`send-prompt-with-image failed (${res.status})`);
    }
    const data = await res.json();
    return parseCourseResponse(data.response, courseName, grade);
  }

  // No file — text-only augmentation via the standard generate endpoint.
  const res = await fetch(`${apiUrl}/api/v1/openai/yaki/generate`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: getBasicAuthHeader(),
    },
    body: JSON.stringify({ prompt }),
  });
  if (!res.ok) {
    throw new Error(`yaki/generate failed (${res.status})`);
  }
  const data = await res.json();
  return parseCourseResponse(data.response, courseName, grade);
};

/* ---------- 3. Persist via the project's add-course API ---------- */
export const addCourse = async (course) => {
  const userId = localStorage.getItem('userId');
  const orgId = localStorage.getItem('orgId');

  if (!userId) {
    const e = new Error('NOT_LOGGED_IN');
    e.code = 'NOT_LOGGED_IN';
    throw e;
  }

  // gradeLabel and courseCategory are for the chat preview / question planning only —
  // keep the payload to the API shape.
  const { gradeLabel, courseCategory, subjectArea, ...payload } = course;

  const res = await fetch(`${apiUrl}/api/course/coursedomain/${userId}&${orgId}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: '*/*',
      'Access-Control-Allow-Origin': '*',
      Authorization: getBasicAuthHeader(),
    },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    throw new Error(`Add course failed (${res.status})`);
  }
  return res.json().catch(() => ({}));
};

/* ============================================================
   Assessment creation
   ------------------------------------------------------------
   After a course is saved we re-fetch it so we get the backend
   assigned ids (courseId, domainId, subDomainId), then split its
   domains/subdomains across N assessments and POST each one to the
   same endpoint assessmentdialog.js uses (/api/assessment/create).
   ============================================================ */

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Accept: '*/*',
  'Access-Control-Allow-Origin': '*',
  Authorization: getBasicAuthHeader(),
});

const parsePlanLimit = (value) => {
  if (value == null || value === '') return null;
  const text = String(value).trim().toLowerCase();
  if (text.includes('unlimited')) return Infinity;
  const parsed = parseInt(text, 10);
  return Number.isFinite(parsed) ? parsed : null;
};

// The dashboard (content.js) caches the user's plan limits here when it loads,
// so the chat assistant validates against them without re-fetching the package.
const PACKAGE_LIMITS_KEY = 'studyak_package_limits';

export const readSavedPackageLimits = () => {
  try {
    const raw = localStorage.getItem(PACKAGE_LIMITS_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

export const fetchPackageUsage = async () => {
  const userId = localStorage.getItem('userId');
  const limits = readSavedPackageLimits();

  // No cached plan limits -> nothing to validate against (fail-open).
  if (!userId || !limits) return null;

  // Only the current usage counts are fetched here; the plan limits come from
  // the cache populated by content.js, so we never re-hit the packages API.
  const [courseRes, assessmentRes] = await Promise.all([
    fetch(`${apiUrl}/api/course/${userId}`, { headers: authHeaders() }),
    fetch(`${apiUrl}/api/Assessment/${userId}`, { headers: authHeaders() }),
  ]);

  if (!courseRes.ok || !assessmentRes.ok) {
    throw new Error('Fetch package usage failed');
  }

  const [courses, assessments] = await Promise.all([
    courseRes.json().catch(() => []),
    assessmentRes.json().catch(() => []),
  ]);

  const courseLimit = parsePlanLimit(limits.courses);
  const assessmentLimit = parsePlanLimit(limits.assessments);
  const courseCount = Array.isArray(courses) ? courses.length : 0;
  const assessmentCount = Array.isArray(assessments) ? assessments.length : 0;

  return {
    packageDetails: limits,
    courseCount,
    assessmentCount,
    courseLimit,
    assessmentLimit,
    courseRemaining:
      courseLimit == null || courseLimit === Infinity
        ? Infinity
        : Math.max(courseLimit - courseCount, 0),
    assessmentRemaining:
      assessmentLimit == null || assessmentLimit === Infinity
        ? Infinity
        : Math.max(assessmentLimit - assessmentCount, 0),
  };
};

// Re-fetch the just-created course (with backend ids) by name.
export const fetchSavedCourse = async (courseName) => {
  const userId = localStorage.getItem('userId');
  const res = await fetch(`${apiUrl}/api/course/coursedomain/${userId}`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error(`Fetch courses failed (${res.status})`);
  const list = await res.json();
  const target = (courseName || '').trim().toLowerCase();
  const matches = (Array.isArray(list) ? list : []).filter(
    (c) => (c.courseName || '').trim().toLowerCase() === target
  );
  if (matches.length === 0) return null;
  // newest first (highest primary key / courseId)
  matches.sort(
    (a, b) =>
      Number(b.id || 0) - Number(a.id || 0) ||
      Number(b.courseId || 0) - Number(a.courseId || 0)
  );
  return matches[0];
};

/* ---------------- chat history (stored in yaki_memory, scope="chat") ---------------- */

// Drops 4-byte (non-BMP) characters such as emoji so a non-utf8mb4 note_value
// column can store the transcript. Applied client-side too, so saving works
// even if the backend hasn't been rebuilt with the same safeguard.
const stripNonBmp = (s) => (typeof s === 'string' ? s.replace(/[\u{10000}-\u{10FFFF}]/gu, '') : s);

/** Upserts the current chat for a user (keyed by chatId) into yaki_memory. */
export const saveChat = async ({ userId, chatId, title, messages }) => {
  if (!userId || !chatId) return;
  const safeMessages = (messages || []).map((m) => ({ ...m, text: stripNonBmp(m.text) }));
  await fetch(`${apiUrl}/api/v1/openai/yaki/memory`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({
      scope: 'chat',
      refId: userId,
      noteKey: chatId,
      noteValue: JSON.stringify({
        title: stripNonBmp(title) || 'Chat',
        messages: safeMessages,
        updatedAt: Date.now(),
      }),
    }),
  }).catch(() => {});
};

/** Lists the user's saved chats, newest first. */
export const listChats = async (userId) => {
  if (!userId) return [];
  const res = await fetch(
    `${apiUrl}/api/v1/openai/yaki/memory?scope=chat&refId=${encodeURIComponent(userId)}`,
    { headers: authHeaders() },
  );
  if (!res.ok) return [];
  const rows = await res.json();
  return (Array.isArray(rows) ? rows : [])
    .map((r) => {
      let parsed = {};
      try { parsed = JSON.parse(r.noteValue || '{}'); } catch (_) { /* ignore */ }
      return {
        chatId: r.noteKey,
        title: parsed.title || 'Chat',
        messages: Array.isArray(parsed.messages) ? parsed.messages : [],
        updatedAt: parsed.updatedAt || Number(r.updatedAt) || 0,
      };
    })
    .sort((a, b) => b.updatedAt - a.updatedAt);
};

/* ---------------- memory files (yaki_memory, scope = enterprise|course|learner) ---------------- */

/** Auto-drafts a hybrid memory .md from a URL or pasted text (not saved). */
export const analyzeMemorySource = async ({ scope, refId, fileName, sourceType, source }) => {
  const res = await fetch(`${apiUrl}/api/v1/openai/yaki/memory/analyze`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ scope, refId, fileName, sourceType, source }),
  });
  if (!res.ok) throw new Error('analyze failed');
  const data = await res.json();
  return (data && data.markdown) || '';
};

/** Lists memory files for a (scope, refId). */
export const listMemoryFiles = async (scope, refId) => {
  if (!scope || !refId) return [];
  const res = await fetch(
    `${apiUrl}/api/v1/openai/yaki/memory?scope=${encodeURIComponent(scope)}&refId=${encodeURIComponent(refId)}`,
    { headers: authHeaders() },
  );
  if (!res.ok) return [];
  const rows = await res.json();
  return (Array.isArray(rows) ? rows : []).map((r) => ({
    scope,
    refId,
    fileName: r.noteKey,
    markdown: r.noteValue || '',
    updatedAt: Number(r.updatedAt) || 0,
  }));
};

/** Upserts one memory file (markdown stored in note_value). */
export const saveMemoryFile = async ({ scope, refId, fileName, markdown }) => {
  if (!scope || !refId || !fileName) return;
  await fetch(`${apiUrl}/api/v1/openai/yaki/memory`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ scope, refId, noteKey: fileName, noteValue: stripNonBmp(markdown) }),
  });
};

/** Deletes one memory file. */
export const deleteMemoryFile = async (scope, refId, fileName) => {
  if (!scope || !refId || !fileName) return;
  await fetch(
    `${apiUrl}/api/v1/openai/yaki/memory?scope=${encodeURIComponent(scope)}&refId=${encodeURIComponent(refId)}&noteKey=${encodeURIComponent(fileName)}`,
    { method: 'DELETE', headers: authHeaders() },
  );
};

/** Deletes a saved chat. */
export const deleteChat = async (userId, chatId) => {
  if (!userId || !chatId) return;
  await fetch(
    `${apiUrl}/api/v1/openai/yaki/memory?scope=chat&refId=${encodeURIComponent(userId)}&noteKey=${encodeURIComponent(chatId)}`,
    { method: 'DELETE', headers: authHeaders() },
  ).catch(() => {});
};

/**
 * Returns ALL of the user's courses matching a name (case/space-insensitive),
 * each with its courseId — so the chat can let the user pick when duplicates exist.
 */
/**
 * Every course on the signed-in instructor's profile, complete with domains and
 * subdomains — the same shape the assessment flow needs, so a course picked here
 * can go straight into buildAssessments. Newest first.
 */
export const listCourses = async () => {
  const userId = localStorage.getItem('userId');
  if (!userId) {
    const e = new Error('NOT_LOGGED_IN');
    e.code = 'NOT_LOGGED_IN';
    throw e;
  }
  const res = await fetch(`${apiUrl}/api/course/coursedomain/${userId}`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error(`Fetch courses failed (${res.status})`);
  const list = await res.json();
  return (Array.isArray(list) ? list : [])
    .filter((c) => c && c.courseId)
    .sort((a, b) => Number(b.id || 0) - Number(a.id || 0));
};

export const findCoursesByName = async (name) => {
  const userId = localStorage.getItem('userId');
  const res = await fetch(`${apiUrl}/api/course/coursedomain/${userId}`, { headers: authHeaders() });
  if (!res.ok) throw new Error(`Fetch courses failed (${res.status})`);
  const list = await res.json();
  const target = (name || '').trim().toLowerCase();
  const matches = (Array.isArray(list) ? list : []).filter(
    (c) => (c.courseName || '').trim().toLowerCase() === target
  );
  // newest first for stable ordering
  matches.sort((a, b) => Number(b.id || 0) - Number(a.id || 0));
  return matches;
};

/**
 * Chat-driven Yaki agent (Integration Plan v0.2). Classifies a free-form
 * message and runs the matching capability: quiz / flashcards (saved as draft),
 * question-bank plan, bank generate (async), adaptive set, review / activate
 * drafts, mistakes, domains preview, or guarded chat.
 */
export const askYakiAgent = async ({ message, courseId, userId }) => {
  const res = await fetch(`${apiUrl}/api/v1/openai/yaki/agent`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({
      message,
      courseId: courseId || null,
      userId: userId || localStorage.getItem('userId'),
    }),
  });
  if (!res.ok) throw new Error(`Yaki agent failed (${res.status})`);
  return res.json();
};

// Total assessments offered as quick picks. Typed requests can go higher; when
// subdomains run out we add bundled domain assessments for the remainder.
export const maxAssessments = (course) => {
  const domains = course?.domains || [];
  const subs = domains.reduce((s, d) => s + (d.subDomain?.length || 0), 0);
  return subs > 0 ? subs : domains.length;
};

// Even round-robin split into `n` buckets (sizes differ by at most 1).
const chunkEvenly = (arr, n) => {
  const groups = Array.from({ length: n }, () => []);
  arr.forEach((item, i) => groups[i % n].push(item));
  return groups;
};

// Strip ids the assessment API doesn't want (matches assessmentdialog.cleanDomains).
const cleanDomains = (domains) =>
  domains.map(({ courseId, ...rest }) => ({
    ...rest,
    subDomain: (rest.subDomain || []).map(({ domainId, ...subRest }) => subRest),
  }));

/**
 * Distribute a course's domains (and their subdomains) across `n` assessments:
 * - First use whole-domain groups when the request fits the domain count.
 * - Then split at subdomain level when more assessments are requested.
 * - If subdomains run out, add bundled domain assessments for the remainder.
 * Returns exactly `n` domain-arrays when the course has content.
 */
export const distributeDomains = (domains, n) => {
  const validDomains = (domains || []).filter(Boolean);
  const requested = parseInt(n, 10);
  if (!validDomains.length || !requested || requested < 1) return [];

  if (requested <= validDomains.length) {
    return chunkEvenly(validDomains, requested).filter((g) => g.length > 0);
  }

  const pairs = [];
  validDomains.forEach((d) => {
    const subs = d.subDomain || [];
    if (subs.length > 0) {
      subs.forEach((s) => pairs.push({ d, s }));
    } else {
      pairs.push({ d, s: null });
    }
  });

  const groupPairsByDomain = (group) => {
    const byDomain = new Map();
    group.forEach(({ d, s }) => {
      const key = d.domainId || d.name;
      if (!byDomain.has(key)) byDomain.set(key, { ...d, subDomain: [] });
      if (s) {
        byDomain.get(key).subDomain.push(s);
      } else {
        byDomain.get(key).subDomain = d.subDomain || [];
      }
    });
    return Array.from(byDomain.values());
  };

  if (requested <= pairs.length) {
    return chunkEvenly(pairs, requested)
      .map(groupPairsByDomain)
      .filter((g) => g.length > 0);
  }

  const subdomainGroups = pairs.map((pair) => groupPairsByDomain([pair]));
  const remaining = requested - subdomainGroups.length;
  const bundledGroups =
    remaining <= validDomains.length
      ? chunkEvenly(validDomains, remaining).filter((g) => g.length > 0)
      : Array.from({ length: remaining }, (_, i) => [
          validDomains[i % validDomains.length],
        ]);

  return [...subdomainGroups, ...bundledGroups].slice(0, requested);
};

// Build `n` assessment payloads from a saved course.
export const buildAssessments = (course, n) => {
  const userId = localStorage.getItem('userId');
  const groups = distributeDomains(course.domains || [], n);
  const codeBase =
    (course.code || course.courseName || 'EXAM')
      .replace(/[^A-Za-z0-9]/g, '')
      .slice(0, 6)
      .toUpperCase() || 'EXAM';

  return groups.map((grp, i) => ({
    name: `${course.courseName} - Assessment ${i + 1}`,
    type: 'Text',
    vendor: course.vendor || 'StudyAK',
    examCode: `${codeBase}-A${i + 1}`,
    examTime: '60',
    totalMarks: '100',
    userId,
    courseId: course.courseId,
    domains: cleanDomains(grp),
  }));
};

// POST a single assessment (same shape/endpoint as assessmentdialog.js).
// Returns { ...assessment, testId } so the question flow can use it.
export const createAssessment = async (assessment) => {
  // assessmentLevel is the assessment's single Bloom's tag. The assessment table has
  // no column for it (it is carried per question, on question.assessment_level), so it
  // is preview-only — keep the payload to the API shape.
  const { assessmentLevel, ...payload } = assessment;
  const res = await fetch(`${apiUrl}/api/assessment/create`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`Create assessment failed (${res.status})`);
  const data = await res.json().catch(() => ({}));
  const testId = data?.testId ?? data?.test_id ?? data?.id ?? null;
  return { ...assessment, testId };
};

/* ============================================================
   Question generation per assessment
   ------------------------------------------------------------
   Mirrors GenerateQuestiondialog.js: pull the assessment's domains
   (/api/AssessmentDomain/{testId}), build a prompt per domain with
   generatePrompt, generate via GenerateQuestions, then save through
   /api/generate_question/{testId}.
   ============================================================ */

// If create didn't return a testId, look it up by exam code / name.
export const resolveTestId = async (assessment) => {
  if (assessment.testId) return assessment.testId;
  const userId = localStorage.getItem('userId');
  const res = await fetch(`${apiUrl}/api/Assessment/${userId}`, {
    headers: authHeaders(),
  });
  if (!res.ok) return null;
  const list = await res.json();
  const arr = Array.isArray(list) ? list : [];
  const match =
    arr.find((a) => (a.examCode || a.exam_code) === assessment.examCode) ||
    arr.find((a) => a.name === assessment.name);
  return match ? match.testId ?? match.test_id ?? match.id ?? null : null;
};

// Fetch the domains stored against an assessment (snake_case fields).
const fetchAssessmentDomains = async (testId) => {
  const res = await fetch(`${apiUrl}/api/AssessmentDomain/${testId}`, {
    headers: authHeaders(),
  });
  if (!res.ok) return [];
  return res.json().catch(() => []);
};

// Keep only keywords that actually occur in the question text. The model
// (YakiPromptService returns keywords as a comma-separated string) sometimes
// adds related terms that aren't in the question; drop any that don't appear
// (case-insensitive) in the question stem so keywords are words in the question only.
const pruneKeywordsToQuestion = (q) => {
  if (!q || typeof q.keywords !== 'string' || !q.question) return q;
  const text = String(q.question).toLowerCase();
  const kept = q.keywords
    .split(',')
    .map((k) => k.trim())
    .filter((k) => k && text.includes(k.toLowerCase()));
  return { ...q, keywords: kept.join(',') };
};

// Pull the JSON array of questions out of the model's raw text response.
const parseQuestions = (raw) => {
  if (!raw) return [];
  const s = String(raw).replace(/```json|```/g, '');
  const start = s.indexOf('[');
  const end = s.lastIndexOf(']');
  if (start === -1 || end === -1) return [];
  try {
    const parsed = JSON.parse(s.substring(start, end + 1));
    return Array.isArray(parsed) ? parsed.map(pruneKeywordsToQuestion) : parsed;
  } catch (e) {
    console.warn('[StudyAK chat] could not parse questions JSON:', e);
    return [];
  }
};

// Split a total question count across domains by their percentage weight.
const splitQuestionCounts = (domains, total) => {
  if (total === domains.length) return domains.map(() => 1);
  const counts = [];
  let assigned = 0;
  domains.forEach((d, i) => {
    let n;
    if (i === domains.length - 1) {
      n = total - assigned;
    } else {
      n = Math.floor((parseFloat(d.percentage || 0) / 100) * total);
      assigned += n;
    }
    counts.push(Math.max(0, n));
  });
  return counts;
};

/**
 * Generate `count` questions for an assessment, split across the question types the
 * user picked in the step-by-step flow.
 *
 * Generation is category-driven, exactly like the automatic flow: one group per
 * chosen type, each rendered from its own DB-editable `course.qtype.*` prompt, and
 * every returned question tagged with its `questionType`. A Passage group generates
 * one shared passage first; an Image group generates one image per question.
 *
 * Returns { testId, questions }.
 */
export const generateQuestions = async (
  assessment,
  count,
  gradeLevel = '',
  assessmentLevels = ['Analyze'],
  questionTypes = []
) => {
  const levels =
    Array.isArray(assessmentLevels) && assessmentLevels.length
      ? assessmentLevels
      : ['Analyze'];
  const types = Array.isArray(questionTypes) ? questionTypes.filter(Boolean) : [];
  const testId = await resolveTestId(assessment);
  if (!testId) {
    const e = new Error('NO_TEST_ID');
    e.code = 'NO_TEST_ID';
    throw e;
  }

  let domains = await fetchAssessmentDomains(testId);
  if (!Array.isArray(domains) || domains.length === 0) {
    // fall back to the in-memory domains, mapped to the snake_case the prompt wants
    domains = (assessment.domains || []).map((d) => ({
      domain_id: d.domainId || d.domain_id,
      name: d.name,
      percentage: d.percentage,
      subDomain: (d.subDomain || []).map((s) => ({
        sub_domain_id: s.subDomainId || s.sub_domain_id,
        name: s.name,
        sub_topics: s.subTopics || s.sub_topics,
      })),
    }));
  }
  if (domains.length === 0) return { testId, questions: [] };

  const total = parseInt(count, 10);
  const plan = questionTypePlan(types, total);
  const yakiDomains = domains.map(toYakiDomain);
  const courseContext = {
    courseId: assessment.courseId,
    courseName: assessment.courseName,
    courseType: assessment.courseType,
  };
  const questions = [];

  for (const item of plan) {
    // A Passage group needs its shared passage first; if that fails, skip the group
    // rather than losing the categories that did generate.
    let passageText = '';
    let passageTitle = '';
    if (item.type === 'Passage') {
      try {
        // eslint-disable-next-line no-await-in-loop
        passageText = await generateAutomaticPassage(
          assessment, courseContext, domains[0], gradeLevel, levels,
        );
        passageTitle = splitPassage(passageText).title;
      } catch (passageError) {
        console.warn(
          '[StudyAK chat] Passage generation failed; skipping passage questions.',
          passageError,
        );
        // eslint-disable-next-line no-continue
        continue;
      }
    }

    const spec = {
      kind: item.type === 'Passage' ? 'scriptQuestionMulti' : 'questionMulti',
      testName: assessment.name,
      mimeType: item.type === 'Image' ? 'Image' : 'Text',
      vendor: assessment.vendor || 'StudyAK',
      language: 'English',
      examTime: assessment.examTime,
      totalMarks: assessment.totalMarks,
      examCode: assessment.examCode,
      gradeLevel,
      assessmentLevel: levels.join(','),
      scenarioBased: item.type === 'Scenario',
      noOfQuestions: item.count,
      courseType: assessment.courseType || '',
      applyDefaults: false,
      questionTypes: [item.type],
      domains: yakiDomains,
      passage: passageText,
      // eslint-disable-next-line no-await-in-loop
      extraPromptText: await automaticTypeInstruction(
        item.type, item.count, undefined, passageTitle,
      ),
    };

    // eslint-disable-next-line no-await-in-loop
    const raw = await GenerateQuestionsFromSpec(spec, {
      courseId: assessment.courseId,
      courseName: assessment.courseName,
      assessmentName: assessment.name,
      model: 'chatbot-questions',
    });

    const generated = parseQuestions(raw).slice(0, item.count).map((question) => {
      // Every question carries its own category so the preview can flag it and
      // saveQuestions can route it to the right case study.
      const { passage: _ignored, ...rest } = question;
      return {
        ...rest,
        questionType: item.type,
        ...(item.type === 'Passage' && passageText ? { passage: passageText } : {}),
      };
    });

    if (item.type === 'Image') {
      // One image per question — never shared between two questions.
      for (const question of generated) {
        const imagePrompt = question.prompt || domains[0]?.name || assessment.name;
        question.prompt = imagePrompt;
        try {
          // eslint-disable-next-line no-await-in-loop
          await wait(1200);
          // eslint-disable-next-line no-await-in-loop
          question.asset = await generateAutomaticImageAsset(imagePrompt);
        } catch (imageError) {
          console.warn(
            '[StudyAK chat] An image failed after retries; continuing without it.',
            imageError,
          );
        }
      }
    }

    questions.push(...generated);
  }

  // Token cost is recorded server-side by the spec endpoint (executeYakiPrompt), so
  // no client-side estimate is needed here.
  return { testId, questions: questions.slice(0, total) };
};

/**
 * Generates questions for an assessment WITHOUT a saved test id — used by the
 * fully-automatic flow so course/assessments/questions can be previewed before
 * anything is persisted. Same prompt path and JSON shape as generateQuestions.
 */
export const generateQuestionsInMemory = async (
  assessment,
  count,
  gradeLevel = '',
  assessmentLevels = ['Analyze'],
  questionTypes = []
) => {
  const levels =
    Array.isArray(assessmentLevels) && assessmentLevels.length ? assessmentLevels : ['Analyze'];
  const types = Array.isArray(questionTypes) ? questionTypes.filter(Boolean) : [];
  const scenario = types.includes('Scenario-based') ? 1 : 0;
  const wantsPassage = types.some((t) => /passage|scenario/i.test(t));
  // Type-mix instruction text is DB-editable (course.questionTypeMix + its
  // passage clause) and rendered server-side.
  const typeInstruction = types.length
    ? await renderServerPrompt('course.questionTypeMix', {
        types: types.join(', '),
        passageClause: wantsPassage ? await renderServerPrompt('course.questionTypeMix.passage') : '',
      })
    : '';

  const domains = (assessment.domains || []).map((d) => ({
    domain_id: d.domainId || d.domain_id || '',
    name: d.name,
    percentage: d.percentage,
    subDomain: (d.subDomain || []).map((s) => ({
      sub_domain_id: s.subDomainId || s.sub_domain_id || '',
      name: s.name,
      sub_topics: s.subTopics || s.sub_topics || '',
    })),
  }));
  if (!domains.length) return [];

  const total = parseInt(count, 10);
  const perDomain = splitQuestionCounts(domains, total);
  const questions = [];
  for (let i = 0; i < domains.length; i++) {
    const n = perDomain[i];
    if (!n || n <= 0) continue;
    const prompt = generatePrompt(
      assessment.name, 'Text', assessment.vendor, assessment.examTime,
      assessment.totalMarks, assessment.examCode, domains[i], n,
      gradeLevel, levels, scenario, 'English', '', '', '', null, typeInstruction
    );
    // eslint-disable-next-line no-await-in-loop
    const resp = await GenerateQuestions(prompt, null, '', '', '', null);
    parseQuestions(resp).forEach((q) => questions.push(q));
  }
  return questions.slice(0, total);
};

/**
 * Exact 10-question mix used only by fully automatic course creation.
 *
 * The split is driven by "courseCategory", which the model returns with the course
 * outline (prompt key: course.generate) — the subject is classified once, when the
 * course is designed, instead of being guessed from keywords in the syllabus.
 * Technical courses use a 5 Direct / 5 Scenario split.
 * Non-Technical subjects get the broad four-type mix: 3 Direct, 3 Scenario,
 * 2 Image and 2 Passage — where the two Image questions share a SINGLE image and
 * the two Passage questions share a SINGLE passage (both saved as their own case
 * study, not embedded in the questions — see generateAutomaticQuestionsInMemory
 * and saveQuestions). Scenario questions are different: the scenario is embedded
 * inside the question text itself and is never stored as a separate case study.
 */
/**
 * Bloom's level for the nth assessment of an automatic course.
 *
 * Each assessment is tagged with EXACTLY ONE level — single-level tagging is what
 * keeps course, assessment and learner analytics comparable. The pool is walked in
 * order so a course still spans the progression: assessment 1 -> Remember,
 * 2 -> Understand, 3 -> Apply, 4 -> Analyze, then repeating.
 */
export const AUTO_LEVEL_POOL = ['Remember', 'Understand', 'Apply', 'Analyze'];

export const automaticAssessmentLevel = (assessmentIndex = 0) => {
  const i = Number.isInteger(assessmentIndex) && assessmentIndex >= 0 ? assessmentIndex : 0;
  return AUTO_LEVEL_POOL[i % AUTO_LEVEL_POOL.length];
};

/**
 * The question categories a user can pick in the step-by-step flow — the same four
 * the automatic flow plans with, so a chosen type really drives generation (a
 * Passage type generates a passage, an Image type generates images).
 */
export const SELECTABLE_QUESTION_TYPES = ['Direct', 'Scenario', 'Image', 'Passage'];

/**
 * Splits a requested question count across the chosen types as evenly as possible,
 * giving the remainder to the earlier types. Types are kept in SELECTABLE order so
 * the plan (and therefore the preview) always reads Direct -> Scenario -> Image ->
 * Passage. An empty/unknown selection falls back to all-Direct.
 */
export const questionTypePlan = (types = [], total = 0) => {
  const wanted = SELECTABLE_QUESTION_TYPES.filter((t) => (types || []).includes(t));
  const count = Number.parseInt(total, 10);
  if (!Number.isFinite(count) || count <= 0) return [];
  if (!wanted.length) return [{ type: 'Direct', count }];

  const base = Math.floor(count / wanted.length);
  let remainder = count % wanted.length;
  return wanted
    .map((type) => {
      const extra = remainder > 0 ? 1 : 0;
      remainder -= extra;
      return { type, count: base + extra };
    })
    .filter((item) => item.count > 0);
};

/**
 * Builds the Non-Technical plan from the editable question mix
 * (System → Question type selection). Types set to 0 are dropped entirely, and the
 * set is capped at the `total` row when the per-type counts exceed it.
 * Returns null when the mix is missing or adds up to nothing, so the caller keeps
 * the built-in 3/3/2/2 default.
 */
const planFromQuestionMix = (mix) => {
  if (!mix) return null;
  const ordered = [
    { type: 'Direct', count: Number(mix.direct) || 0 },
    { type: 'Scenario', count: Number(mix.scenario) || 0 },
    { type: 'Image', count: Number(mix.image) || 0 },
    { type: 'Passage', count: Number(mix.passage) || 0 },
  ].filter((item) => item.count > 0);
  if (!ordered.length) return null;

  const total = Number(mix.total) || 0;
  if (total <= 0) return ordered;

  // Trim from the last type down so the set never exceeds the configured total.
  let running = 0;
  const capped = [];
  for (const item of ordered) {
    if (running >= total) break;
    const count = Math.min(item.count, total - running);
    running += count;
    capped.push({ ...item, count });
  }
  return capped.length ? capped : null;
};

export const automaticQuestionPlan = (course = {}, questionMix = null) => {
  // K-12 ELA is assessed only through passage- and image-based items, each pairing a
  // scenario with a direct question about the passage / its own image. Image questions
  // get one image EACH, so the split leans on the (single) shared passage to keep the
  // image count — and therefore generation cost and time — sane.
  if (isK12Ela(course)) {
    return [
      { type: 'Passage', count: 7, variant: 'ela' },
      { type: 'Image', count: 3, variant: 'ela' },
    ];
  }

  if (normaliseCourseCategory(course.courseCategory) === 'Technical') {
    return [
      { type: 'Direct', count: 5 },
      { type: 'Scenario', count: 5 },
    ];
  }

  // Non-Technical: the mix is configurable in System → Question type selection, so
  // it can be retuned (or a type switched off entirely) without a redeploy. The
  // 3/3/2/2 below is only the fallback when that table can't be read.
  return planFromQuestionMix(questionMix) || [
    { type: 'Direct', count: 3 },
    { type: 'Scenario', count: 3 },
    { type: 'Image', count: 2 },
    { type: 'Passage', count: 2 },
  ];
};

const toYakiDomain = (domain) => ({
  domainId: domain.domainId || domain.domain_id || '',
  name: domain.name,
  percentage: domain.percentage,
  subDomain: (domain.subDomain || []).map((sub) => ({
    subDomainId: sub.subDomainId || sub.sub_domain_id || '',
    name: sub.name,
    subTopics: sub.subTopics || sub.sub_topics || '',
  })),
});

// Per-type instruction for the automatic course flow. The text is DB-editable
// (keys: course.qtype.direct|scenario|image|passage, plus the K-12 ELA variants
// course.qtype.passage.ela and course.qtype.image.ela) and rendered server-side.
const automaticTypeInstruction = (type, count, variant, passageTitle = '') => {
  const known = ['direct', 'scenario', 'image', 'passage'];
  const t = String(type).toLowerCase();
  const base = `course.qtype.${known.includes(t) ? t : 'direct'}`;
  // Only Passage and Image have an ELA variant; anything else keeps the base prompt.
  const key = variant === 'ela' && (t === 'passage' || t === 'image')
    ? `${base}.ela`
    : base;
  // passageTitle is only meaningful for the passage prompts; unknown placeholders
  // render as an empty string server-side, so passing it everywhere is harmless.
  return renderServerPrompt(key, { type, count, passageTitle });
};

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const generateAutomaticImageAsset = async (prompt) => {
  if (!prompt) return '';
  // userId1 is the original instructor id used by the impersonation flow.
  // Falling back to it prevents the backend entitlement guard from receiving
  // an empty id while the automatic draft is being generated.
  const userId = localStorage.getItem('userId') || localStorage.getItem('userId1') || '';
  if (!userId) {
    throw new Error('Image generation requires a logged-in instructor userId.');
  }
  let lastError;
  for (let attempt = 0; attempt < 3; attempt++) {
    // Space provider calls out to avoid burst rate limits across assessments.
    if (attempt > 0) {
      // eslint-disable-next-line no-await-in-loop
      await wait(2000 * (attempt + 1));
    }
    try {
      // eslint-disable-next-line no-await-in-loop
      const res = await fetch(`${apiUrl}/api/v1/openai/generate-image`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ prompt, userId }),
      });
      // eslint-disable-next-line no-await-in-loop
      const base64 = await res.text();
      if (!res.ok) {
        throw new Error(`Generate image failed (${res.status}): ${base64 || res.statusText}`);
      }
      const clean = base64.replace(/^"|"$/g, '');
      if (!clean) throw new Error('Generate image returned an empty response.');
      return clean.startsWith('data:image/') ? clean : `data:image/png;base64,${clean}`;
    } catch (error) {
      lastError = error;
    }
  }
  throw lastError || new Error('Image generation failed after 3 attempts.');
};

const generateAutomaticPassage = async (assessment, course, domains, gradeLevel, assessmentLevels) => {
  // Accepts one domain or several — the all-domains assessment draws its passage
  // from the whole course, a per-domain one from just its own domain.
  const list = Array.isArray(domains) ? domains : [domains];
  const raw = await GenerateQuestionsFromSpec({
    kind: 'passage',
    title: list.map((d) => d?.name).filter(Boolean).join(', '),
    testName: assessment.name,
    mimeType: 'Text',
    vendor: assessment.vendor || 'StudyAK',
    language: 'English',
    examTime: assessment.examTime,
    totalMarks: assessment.totalMarks,
    examCode: assessment.examCode,
    gradeLevel,
    assessmentLevel: assessmentLevels.join(','),
    courseType: course?.courseType || '',
    applyDefaults: false,
    domains: list.map(toYakiDomain),
  }, {
    courseId: course?.courseId,
    courseName: course?.courseName,
    assessmentName: assessment.name,
    model: 'automatic-course-passage',
  });
  const passage = String(raw || '').trim();
  if (!passage) throw new Error('Automatic passage generation returned an empty passage.');
  return passage;
};

/**
 * Generates the exact automatic-course category mix for one assessment.
 *
 * Fast path: ONE request returns every category, the shared passage and each
 * image's description together. Only categories the model came up short on are
 * re-requested individually, and the images are generated in parallel — so the
 * usual case is a single question call plus the images, instead of a call per
 * category, a passage call, and the images one after another.
 *
 * `onProgress({ step, detail })` reports each stage for the progress indicator.
 */
export const generateAutomaticQuestionsInMemory = async (
  assessment,
  course,
  gradeLevel = '',
  assessmentLevels = ['Analyze'],
  onProgress = () => {},
  questionMix = null,
) => {
  const domains = assessment.domains || [];
  const domain = domains[0];
  if (!domain) return [];

  // A per-domain assessment carries one domain; the course-wide "Pre Evaluation"
  // assessment carries them all, and must draw questions from every one of them.
  const isMultiDomain = domains.length > 1;
  const multiKind = (kind) => (isMultiDomain ? `${kind}Multi` : kind);

  const plan = automaticQuestionPlan(course, questionMix);
  const planTotal = plan.reduce((sum, item) => sum + item.count, 0);
  const wanted = new Map(plan.map((item) => [item.type, item.count]));
  const baseSpec = {
    testName: assessment.name,
    vendor: assessment.vendor || 'StudyAK',
    language: 'English',
    examTime: assessment.examTime,
    totalMarks: assessment.totalMarks,
    examCode: assessment.examCode,
    gradeLevel,
    assessmentLevel: assessmentLevels.join(','),
    courseType: course?.courseType || '',
    applyDefaults: false,
    ...(isMultiDomain
      ? { domains: domains.map(toYakiDomain) }
      : { domain: toYakiDomain(domain) }),
  };
  const costContext = {
    courseId: course?.courseId,
    courseName: course?.courseName,
    assessmentName: assessment.name,
    model: 'automatic-course',
  };

  /** Normalises one model item onto the shape the rest of the flow expects. */
  const normalise = (question, type) => {
    const { passage: rawPassage, ...rest } = question;
    return {
      ...rest,
      questionType: type,
      mimeType: type === 'Image' ? 'Image' : (question.mimeType || 'Text'),
      // Only Passage items keep a passage. A Scenario embeds its scenario in the
      // question text and must never carry one (it would become a case study).
      ...(type === 'Passage' && rawPassage ? { passage: String(rawPassage).trim() } : {}),
    };
  };

  const byType = new Map(plan.map((item) => [item.type, []]));
  const keep = (question, type) => {
    const bucket = byType.get(type);
    if (bucket && bucket.length < (wanted.get(type) || 0)) bucket.push(normalise(question, type));
  };

  // ---- Fast path: the WHOLE assessment in one response ----------------------
  // One call returns every category, the shared passage and each image's own
  // description, replacing a call per category plus a separate passage call.
  onProgress({ step: 'questions', detail: 'Writing questions' });
  try {
    const mixList = plan.map((item) => `${item.count} ${item.type}`).join(', ');
    const raw = await GenerateQuestionsFromSpec({
      ...baseSpec,
      kind: 'combined',
      mimeType: 'Text',
      noOfQuestions: planTotal,
      questionTypes: plan.map((item) => item.type),
      extraPromptText: await renderServerPrompt('course.qtype.combined', {
        mixList,
        total: planTotal,
      }),
    }, costContext);
    parseQuestions(raw).forEach((question) => {
      const match = plan.find((item) => item.type === question.questionType);
      if (match) keep(question, match.type);
    });
  } catch (combinedError) {
    console.warn(
      '[StudyAK chat] The combined question request failed; falling back to per-category generation.',
      combinedError,
    );
  }

  // ---- Top-up: only the categories the single response came up short on -----
  const short = plan.filter((item) => (byType.get(item.type) || []).length < item.count);
  for (const item of short) {
    const bucket = byType.get(item.type);
    const remaining = item.count - bucket.length;
    let sharedPassage = bucket.find((q) => q.passage)?.passage || '';
    let passageTitle = splitPassage(sharedPassage).title;
    if (item.type === 'Passage' && !sharedPassage) {
      try {
        // eslint-disable-next-line no-await-in-loop
        sharedPassage = await generateAutomaticPassage(
          assessment, course, domains, gradeLevel, assessmentLevels,
        );
        passageTitle = splitPassage(sharedPassage).title;
      } catch (passageError) {
        console.warn(
          '[StudyAK chat] Passage generation failed; skipping passage questions for this assessment.',
          passageError,
        );
        // eslint-disable-next-line no-continue
        continue;
      }
    }
    try {
      // eslint-disable-next-line no-await-in-loop
      const extraPromptText = await automaticTypeInstruction(
        item.type, remaining, item.variant, passageTitle,
      );
      // eslint-disable-next-line no-await-in-loop
      const raw = await GenerateQuestionsFromSpec({
        ...baseSpec,
        kind: multiKind(item.type === 'Passage' ? 'scriptQuestion' : 'question'),
        mimeType: item.type === 'Image' ? 'Image' : 'Text',
        scenarioBased: item.type === 'Scenario',
        noOfQuestions: remaining,
        questionTypes: [item.type],
        passage: sharedPassage,
        extraPromptText,
      }, costContext);
      parseQuestions(raw).slice(0, remaining).forEach((question) => keep(
        item.type === 'Passage' && sharedPassage
          ? { ...question, passage: sharedPassage }
          : question,
        item.type,
      ));
    } catch (topUpError) {
      console.warn(
        `[StudyAK chat] Top-up for ${item.type} questions failed; keeping what was generated.`,
        topUpError,
      );
    }
  }

  // Every Passage question shares ONE passage — use the first the model produced.
  const passageBucket = byType.get('Passage') || [];
  const sharedPassage = passageBucket.find((q) => q.passage)?.passage || '';
  if (sharedPassage) passageBucket.forEach((question) => { question.passage = sharedPassage; });

  // ---- Images: one per question, generated in parallel ----------------------
  const imageBucket = byType.get('Image') || [];
  if (imageBucket.length) {
    onProgress({ step: 'images', detail: `Creating ${imageBucket.length} image(s)` });
    await Promise.all(imageBucket.map(async (question, index) => {
      const imagePrompt = question.prompt || domain.name;
      question.prompt = imagePrompt;
      try {
        // Small stagger so a burst doesn't trip the provider's rate limit, while
        // still overlapping the requests instead of running them back to back.
        await wait(index * 250);
        question.asset = await generateAutomaticImageAsset(imagePrompt);
      } catch (imageError) {
        console.warn(
          '[StudyAK chat] An automatic image failed after retries; continuing without it.',
          imageError,
        );
      }
    }));
  }

  plan.forEach((item) => {
    const got = (byType.get(item.type) || []).length;
    if (got < item.count) {
      console.warn(
        `[StudyAK chat] Generated ${got} of ${item.count} ${item.type} questions; keeping what was generated.`,
      );
    }
  });

  // Plan order, so the preview reads Direct -> Scenario -> Image -> Passage.
  return plan.flatMap((item) => byType.get(item.type) || []).slice(0, planTotal);
};

// Persist generated questions (same endpoint as GenerateQuestiondialog.handleSave).
//
// The automatic flow produces at most ONE shared passage and ONE shared image per
// assessment. Each is saved as its own case_studies row (via the QuestionPayload
// batch: a non-empty `passage` makes the backend create the case study and link
// its id to every question in the batch), and is stripped off the questions
// themselves so it is stored once and merely referenced. Direct/Scenario
// questions go in a plain batch (Scenario keeps its own per-question passage).
export const saveQuestions = async (testId, questions) => {
  const list = Array.isArray(questions) ? questions : [];

  const passageQuestions = list.filter((q) => q.questionType === 'Passage' && q.passage);
  const imageQuestions = list.filter(
    (q) => q.questionType === 'Image' && typeof q.asset === 'string' && q.asset,
  );
  const otherQuestions = list.filter(
    (q) => !passageQuestions.includes(q) && !imageQuestions.includes(q),
  );

  // Upload one image per image question (base64 -> blob URL). The question.asset DB
  // column is only 845 characters, so the image is stored in the case study's link
  // column, not on the question. An http(s) asset is already a blob URL.
  const uploadImageAsset = async (asset) => {
    if (!asset.startsWith('data:image/')) return asset;
    const uploadRes = await fetch(`${apiUrl}/api/uploadBase64Image`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ base64: asset }),
    });
    const uploadBody = await uploadRes.text();
    if (!uploadRes.ok) {
      throw new Error(`Image upload failed (${uploadRes.status}): ${uploadBody || uploadRes.statusText}`);
    }
    let url = '';
    try {
      url = JSON.parse(uploadBody).fileUrl;
    } catch (error) {
      throw new Error('Image upload returned an invalid response.');
    }
    if (!url) throw new Error('Image upload did not return a blob URL.');
    return url;
  };

  // Drop the shared-case-study payload (passage text / image asset + prompt) from
  // the questions — the case study carries it, so it is not duplicated per row.
  const stripSharedFields = ({ passage, asset, prompt, ...question }) => question;

  const batches = [];
  if (otherQuestions.length) {
    // Direct and Scenario questions have no shared case study. A Scenario's
    // scenario text is embedded inside the question itself, so we strip any stray
    // passage/asset/prompt to guarantee the backend never creates a case study
    // (per-question or shared) for them.
    batches.push({ passage: '', questions: otherQuestions.map(stripSharedFields), caseStudyOption: '', mediaUrl: '' });
  }
  if (passageQuestions.length) {
    batches.push({
      passage: passageQuestions[0].passage,
      questions: passageQuestions.map(stripSharedFields),
      caseStudyOption: '',
      mediaUrl: '',
    });
  }
  // ONE case study per image question — an image is never shared by two questions.
  // caseStudyOption 'generateImage' + non-empty passage makes the backend create an
  // image case study (link = mediaUrl) for the single question in that batch.
  const imagelessQuestions = [];
  for (const question of imageQuestions) {
    let url = '';
    try {
      // eslint-disable-next-line no-await-in-loop
      url = await uploadImageAsset(question.asset);
    } catch (uploadError) {
      // Best-effort, matching generation: one failed upload must not discard the
      // whole assessment — that question is saved without its visual.
      console.warn(
        '[StudyAK chat] An image upload failed; saving that question without its image.',
        uploadError,
      );
    }
    if (url) {
      batches.push({
        passage: question.prompt || 'Image',
        questions: [stripSharedFields(question)],
        caseStudyOption: 'generateImage',
        mediaUrl: url,
      });
    } else {
      imagelessQuestions.push(question);
    }
  }
  if (imagelessQuestions.length) {
    // Image generation or upload was unavailable — save those questions plainly.
    batches.push({
      passage: '',
      questions: imagelessQuestions.map(stripSharedFields),
      caseStudyOption: '',
      mediaUrl: '',
    });
  }

  const results = [];
  for (const payload of batches) {
    // A passage batch uses the backend's top-level QuestionPayload.passage,
    // creating one case_studies row whose id is assigned to every question.
    // eslint-disable-next-line no-await-in-loop
    const res = await fetch(`${apiUrl}/api/generate_question/${testId}`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      // eslint-disable-next-line no-await-in-loop
      const errorBody = await res.text();
      throw new Error(`Save questions failed (${res.status}): ${errorBody || res.statusText}`);
    }
    // eslint-disable-next-line no-await-in-loop
    results.push(await res.json().catch(() => ({})));
  }
  return results;
};
